import { useState } from 'react';
import { ButtonSecundario, Input } from '../ui'; // Componentes reutilizables
import { PopupProps } from '@/types'; // Tipos de props de los popups
import { isValidPhone } from '../validation/validations'; // Función de validación de teléfono
import { PopupStilosCss } from '../styles'; // Estilos CSS específicos del popup

/**
 * PopupTelefonos
 * 
 * Modal para añadir un número de teléfono.
 * Valida que el teléfono tenga exactamente 9 dígitos antes de enviarlo.
 * 
 * Props extendidas de PopupProps:
 * - onClose: función para cerrar el popup
 * - onAddPhone: callback para enviar el teléfono al componente padre
 * - className: clases CSS opcionales
 */
const PopupTelefonos: React.FC<PopupProps> = ({ onClose, onAddPhone, className }) => {
    const [newPhone, setNewPhone] = useState(''); // Estado local para el input del teléfono

    // Maneja cambios en el input del teléfono
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        // Solo permite hasta 9 dígitos numéricos
        if (/^\d{0,9}$/.test(value)) {
            setNewPhone(value);
        }
    };

    // Función que valida y envía el teléfono
    const handleAddPhone = () => {
        if (isValidPhone(newPhone)) {
            // Llama al callback del padre con el nuevo número
            onAddPhone?.({ numero_telefono: newPhone });
            setNewPhone(''); // Resetea input
            onClose?.(); // Cierra el popup
        } else {
            alert('El número de teléfono debe tener exactamente 9 dígitos.');
        }
    };

    return (
        // Fondo oscuro que cubre toda la pantalla y centra el popup
        <div className={`fixed inset-0 ${PopupStilosCss.popupTelefono} flex items-center justify-center bg-black bg-opacity-50 z-50`}>
            {/* Contenedor del popup */}
            <div className={`${className || ''} space-y-4 bg-zinc-400 shadow-xl rounded-md p-8 relative ${PopupStilosCss.popupTelefono} overflow-auto`}>
                
                {/* Título */}
                <h2 className="text-xl text-black font-bold">Añadir Teléfono</h2>

                {/* Input del teléfono */}
                <div className="relative">
                    <Input
                        type="text"
                        name="numero_telefono"
                        value={newPhone}
                        onChange={handlePhoneChange}
                        placeholder="Número de teléfono..."
                        className="py-2 rounded-md border border-gray-600 focus:border-black text-sm w-full"
                    />
                    {/* Mensaje de error si el número tiene 9 dígitos pero no es válido */}
                    {newPhone.length === 9 && !isValidPhone(newPhone) && (
                        <p className="absolute text-red-600 text-sm mt-1 -top-2.5 left-0 bg-white bg-opacity-90 px-2 py-1 rounded shadow z-10">
                            Número no válido, prueba de nuevo con otro número.
                        </p>
                    )}
                </div>

                {/* Botones Añadir y Cancelar */}
                <div className="flex space-x-2 text-center pb-4">
                    <ButtonSecundario
                        onClick={handleAddPhone}
                        isAddButton={true}
                        className="h-10 w-3/6 transition-colors items-center bg-zinc-600 shadow-md shadow-zinc-700 rounded-md duration-400 border hover:border-green-600 active:font-extrabold active:bg-white active:text-black text-white"
                    >
                        Añadir
                    </ButtonSecundario>

                    <ButtonSecundario
                        onClick={onClose}
                        className="h-10 w-3/6 transition-colors items-center bg-zinc-600 shadow-md shadow-zinc-700 rounded-md duration-400 border hover:border-red-600 active:bg-white active:text-black text-white active:font-extrabold"
                    >
                        Cancelar
                    </ButtonSecundario>
                </div>
            </div>
        </div>
    );
};

export default PopupTelefonos;
